import { useEffect, useLayoutEffect, useState } from 'react';

/**
 * THE positioning of a panel PORTALLED to `<body>` and anchored to a trigger —
 * extracted 2026-10-19 out of `components/ui/Dropdown.js`'s `portal` path,
 * verbatim in behaviour, so a second popup (`components/ui/SuggestInput.js`,
 * the gag Unit / Primary effect combobox) could share it instead of copying it.
 * The REASONS behind every rule below are in Dropdown's docblock (the
 * clip-not-z-index incident, `KebabMenu` precedent) — read that before
 * changing anything here.
 *
 * What it does, while `enabled && open`:
 *  - `primeCoords()` — a first guess from the trigger rect so the panel never
 *    paints at 0,0. Call it in the same handler that opens the panel.
 *  - before paint, refines from the panel's MEASURED box: `align` picks which
 *    trigger edge the panel lines up with; clamped 8px inside the viewport;
 *    flips ABOVE the trigger when opening downward would overflow the bottom.
 *  - dismisses (`onDismiss`) on any scroll (capture phase — inner scrollports
 *    too) or resize. 🔴 Close, never track: a fixed panel would detach from a
 *    row that scrolls away. 🪤 A scroll that starts INSIDE the panel (its own
 *    option list) is ignored, and `e.target` may be `window`, which
 *    `Node.contains` would throw on — hence the `instanceof Node` test.
 *
 * It renders nothing and owns no open state: the caller owns `open`, the
 * refs and the portal, and applies `coords` as `position: fixed` at the
 * contract's escape tier (`zIndex: 60`, `ui/Toolbar`'s z-index contract).
 *
 * @param {object}  p
 * @param {boolean} p.open
 * @param {boolean} p.enabled     false → every effect is a no-op, coords stay null
 * @param {'left'|'right'} [p.align='left']
 * @param {{current: HTMLElement|null}} p.triggerRef
 * @param {{current: HTMLElement|null}} p.panelRef
 * @param {() => void} p.onDismiss called on scroll/resize; must close the panel
 * @param {{current: HTMLElement|null}} [p.ignoreScrollWithin] OPT-IN (2026-10-19,
 *   `ui/SuggestInput`): a scroll whose target is inside this element is not a
 *   page scroll. 🪤 A focused text `<input>` fires `scroll` on ITSELF when its
 *   text scrolls (focus, typing) — measured in Chromium: the list opened and
 *   closed in the same click. jsdom fires no scroll, so no unit test saw it.
 *   `ui/Dropdown` passes nothing: a button trigger never scrolls.
 * @returns {{ coords: {top:number,left:number}|null, primeCoords: () => void }}
 */
export default function useAnchoredPortalPanel({ open, enabled, align = 'left', triggerRef, panelRef, onDismiss, ignoreScrollWithin = null }) {
  const [coords, setCoords] = useState(null);

  const primeCoords = () => {
    if (enabled && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: r.left });
    }
  };

  useLayoutEffect(() => {
    if (!open || !enabled) return;
    const btn = triggerRef.current;
    const panel = panelRef.current;
    if (!btn || !panel) return;
    const r = btn.getBoundingClientRect();
    const pw = panel.offsetWidth;
    const ph = panel.offsetHeight;
    const pad = 8;
    // `align` keeps its anchored meaning: which EDGE of the trigger the panel
    // lines up with.
    let left = align === 'right' ? r.right - pw : r.left;
    left = Math.max(pad, Math.min(left, window.innerWidth - pw - pad));
    // Flip above the trigger when opening downward would overflow the viewport
    // bottom — the last-row case, and the reason a bare `mt-1` is not enough.
    let top = r.bottom + 4;
    if (top + ph > window.innerHeight - pad && r.top - ph - 4 >= pad) {
      top = r.top - ph - 4;
    }
    // Bail out when nothing moved, so measure→setState→re-measure cannot oscillate.
    setCoords((c) => (c && c.top === top && c.left === left ? c : { top, left }));
  }, [open, enabled, align]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !enabled) return undefined;
    // 🪤 Capture phase so a scroll on ANY inner scrollport dismisses the panel,
    // not just the window — the Breakdown's table is an inner region. But that
    // also catches the panel's OWN option list scrolling, which would dismiss
    // it under the user's finger; hence the containment test.
    // 🪤 `e.target` is NOT always a Node: a scroll event dispatched on `window`
    // targets `window`, and `Node.contains(window)` THROWS a TypeError rather
    // than returning false — an uncaught exception inside a capture-phase
    // listener bound to every scroll on the page. Measured, not guessed. The
    // `instanceof Node` test is what makes the containment check safe; a
    // document-targeted page scroll passes it and correctly closes the panel.
    const onScroll = (e) => {
      const t = e.target;
      if (t instanceof Node && panelRef.current?.contains(t)) return;
      if (t instanceof Node && ignoreScrollWithin?.current?.contains(t)) return;
      onDismiss();
    };
    const onResize = () => onDismiss();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return { coords, primeCoords };
}
